import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { bugsnagNotify } from 'utilities/bugsnag';

import * as api from '@test_prep/app/ai_tutor/api';
import { AvailableSkill } from '@test_prep/app/ai_tutor/types';
import ErrorMessage from '@test_prep/app/ai_tutor/components/error_message';
import { ArrowIcon, SkillIcon } from '@test_prep/app/ai_tutor/components/icons';

import styles from './style.module.scss';

interface Props {
  dataFetchKey: string;
  onSelected: (availableSkill: AvailableSkill) => void;
}

const Home: React.FC<Props> = (props) => {
  const [availableSkills, setAvailableSkills] = useState<AvailableSkill[]>(null);
  const [error, setError] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if (!props.dataFetchKey) return;

    fetchAvailableSkills();
    // TODO - skill list needs to update on a SPA navigate to a new page
  }, [props.dataFetchKey]);

  const generalSkills = [...(availableSkills || []).filter((availableSkill) => !availableSkill.domain.resourceType && !availableSkill.skill?.resourceType)];
  const resourceBasedSkills = (availableSkills || []).filter((availableSkill) => !!availableSkill.domain.resourceType || !!availableSkill.skill?.resourceType);

  const fetchAvailableSkills = () => {
    setError(false);
    setIsFetching(true);

    return api.fetchAvailableAITutoringSessionSkills().then((availableSkills) => {
      setAvailableSkills(availableSkills);
      setIsFetching(false);
    }).catch((error) => {
      setError(true);
      setIsFetching(false);
      bugsnagNotify(error);
    });
  };

  const resourceName = (availableSkill: AvailableSkill) => {
    const resourceType = availableSkill.domain.resourceType || availableSkill.skill?.resourceType;

    if (resourceType === 'Answer') {
      return 'Question';
    } else {
      return resourceType;
    }
  };

  if(error) {
    return (
      <ErrorMessage onRetry={fetchAvailableSkills} />
    );
  }

  return (
    <>
      <div
        className={cx(
          styles.refreshIndicator,
          'd-flex flex-1 justify-content-center',
          { [styles.hidden]: !isFetching }
        )}
      >
        <FontAwesomeIcon icon="spinner" className="fa-pulse" />
      </div>

      {
        availableSkills && (
          <div className="d-flex flex-column" style={{ gap: 15 }}>
            {/*TODO - resume current session*/}
            {
              resourceBasedSkills.length !== 0 && (
                <div className="d-flex flex-column" style={{ gap: 8 }}>
                  <div className="text-bold" style={{ color: '#9AA0A9', fontSize: 12 }}>
                    Related to this {resourceName(resourceBasedSkills[0])}
                  </div>

                  <div className="d-flex flex-column" style={{ gap: 15 }}>
                    {
                      resourceBasedSkills.map((availableSkill: AvailableSkill) => (
                        <React.Fragment key={availableSkill.skill?.name || availableSkill.domain.name}>
                          <AvailableSkillOption
                            availableSkill={availableSkill}
                            onSelected={() => props.onSelected(availableSkill)}
                          />
                          <hr className="u-margin-A-n" style={{ backgroundColor: '#F5F5F6' }} />
                        </React.Fragment>
                      ))
                    }
                  </div>
                </div>
              )
            }

            {
              generalSkills.length !== 0 && (
                <div className="d-flex flex-column" style={{ gap: 8 }}>
                  {
                    resourceBasedSkills.length !== 0 && (
                      <div className="text-bold" style={{ color: '#9AA0A9', fontSize: 12 }}>
                        General Help
                      </div>
                    )
                  }

                  <div className="d-flex flex-column" style={{ gap: 15 }}>
                    {
                      generalSkills.map((availableSkill: AvailableSkill) => (
                        <React.Fragment key={availableSkill.skill?.name || availableSkill.domain.name}>
                          <AvailableSkillOption
                            availableSkill={availableSkill}
                            onSelected={() => props.onSelected(availableSkill)}
                          />
                          <hr className="u-margin-A-n" style={{ backgroundColor: '#F5F5F6' }} />
                        </React.Fragment>
                      ))
                    }
                  </div>
                </div>
              )
            }

            <div style={{ color: '#717A87', fontSize: 12 }}>
              Not finding what you're looking for?
              {' '}<a href="mailto:help@magoosh.com" style={{ color: '#717A87' }}>Send us an email.</a>
            </div>
          </div>
        )
      }
    </>
  );
};

interface AvailableSkillOptionProps {
  availableSkill: AvailableSkill;
  onSelected: () => void;
}

const AvailableSkillOption = (props: AvailableSkillOptionProps) => {
  const description = props.availableSkill.skill?.description || props.availableSkill.domain.description;
  const name = props.availableSkill.skill?.name || props.availableSkill.domain.name;

  return (
    <div
      className={cx(styles.availableSkill, 'align-items-center d-flex flex-1 gap-s')}
      onClick={props.onSelected}
      style={{ cursor: 'pointer' }}
    >
      <div>
        <SkillIcon domain={props.availableSkill.domain} size="lg" skill={props.availableSkill.skill} />
      </div>

      <div className="d-flex flex-1 flex-column" style={{ gap: 3 }}>
        <div className="small">
          {name}
        </div>

        <div style={{ color: '#717A87', fontSize: 12 }}>
          {description}
        </div>
      </div>

      <div className={styles.availableSkillArrowIcon}>
        <ArrowIcon size={20} />
      </div>
    </div>
  );
}

export default Home;
